import React, { useState, useEffect, useCallback } from 'react'
import EmptyTable from './EmptyTable'
import ReactCountryFlag from "react-country-flag"
import { useAuth } from "./api/authStore"
import { countryNames } from './config/countryNames'
import Visibility from '@mui/icons-material/Visibility'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import ProxyDetailsDialog from './ProxyDetailsDialog'
import useMobile from './hooks/useMobile'
import MobileMenu from './components/MobileMenu'
import AnimatedBurgerIcon from './components/AnimatedBurgerIcon'
import MobileActiveProxiesTable from './components/MobileActiveProxiesTable'
import NoteEditor from './components/NoteEditor'
import moment from 'moment'

// Компонент Tooltip
const Tooltip = ({ title, children, placement = "top" }) => {
    const [show, setShow] = useState(false)
    
    return (
        <div 
            style={{ position: 'relative', display: 'inline-block' }}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && (
                <div style={{
                    position: 'absolute',
                    bottom: placement === 'top' ? '100%' : 'auto',
                    top: placement === 'bottom' ? '100%' : 'auto',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'var(--dark-gray)',
                    color: 'var(--text-light)',
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-xs)',
                    whiteSpace: 'nowrap',
                    zIndex: 1000,
                    boxShadow: 'var(--shadow-card)',
                    border: '1px solid var(--light-gray)',
                    marginBottom: placement === 'top' ? '8px' : '0',
                    marginTop: placement === 'bottom' ? '8px' : '0'
                }}>
                    {title}
                    <div style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '5px solid transparent',
                        borderRight: '5px solid transparent',
                        ...(placement === 'top' ? {
                            top: '100%',
                            borderTop: '5px solid var(--light-gray)'
                        } : {
                            bottom: '100%',
                            borderBottom: '5px solid var(--light-gray)'
                        })
                    }} />
                </div>
            )}
        </div>
    )
}

// Компонент пагинации для десктопа
const PaginationActions = ({ page, rowsPerPage, count, onPageChange }) => {
    const safePage = isNaN(page) ? 0 : page
    const safeRowsPerPage = isNaN(rowsPerPage) ? 10 : rowsPerPage
    const safeCount = isNaN(count) ? 0 : count
    
    const totalPages = safeRowsPerPage === 0 ? 0 : Math.ceil(safeCount / safeRowsPerPage)
    
    const handleFirstPage = () => onPageChange(null, 0)
    const handlePrevPage = () => onPageChange(null, Math.max(0, safePage - 1))
    const handleNextPage = () => onPageChange(null, Math.min(totalPages - 1, safePage + 1))
    const handleLastPage = () => onPageChange(null, Math.max(0, totalPages - 1))

    return (
        <div style={{ display: 'flex', gap: '4px' }}>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage === 0 ? 0.5 : 1
                }}
                onClick={handleFirstPage}
                disabled={safePage === 0}
            >
                <KeyboardDoubleArrowLeft style={{ fontSize: '14px' }} />
            </button>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage === 0 ? 0.5 : 1
                }}
                onClick={handlePrevPage}
                disabled={safePage === 0}
            >
                <KeyboardArrowLeft style={{ fontSize: '14px' }} />
            </button>
            
            <span style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                margin: '0 var(--spacing-sm)',
                whiteSpace: 'nowrap',
                minWidth: '80px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center'
            }}>
                {isNaN(page) ? 0 : page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, safeCount)} of {safeCount}
            </span>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage >= totalPages - 1 ? 0.5 : 1
                }}
                onClick={handleNextPage}
                disabled={safePage >= totalPages - 1}
            >
                <KeyboardArrowRight style={{ fontSize: '14px' }} />
            </button>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage >= totalPages - 1 ? 0.5 : 1
                }}
                onClick={handleLastPage}
                disabled={safePage >= totalPages - 1}
            >
                <KeyboardDoubleArrowRight style={{ fontSize: '14px' }} />
            </button>
        </div>
    )
}

const ActiveProxiesTable = (props) => {
    const { isMobile, isTablet } = useMobile()
    const [selectedRow, setSelectedRow] = useState([])
    const [isRefund, setIsRefund] = useState(false)
    const activeProxiesList = props.list?.leases || []
    const [openDetails, setOpenDetails] = React.useState(false)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    
    // Состояние для пагинации
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)
    const [totalProxies, setTotalProxies] = React.useState(0)
    
    // Состояние для заметок
    const [editingNoteFor, setEditingNoteFor] = useState(null)
    const [notes, setNotes] = useState({})
    

    // CSS стили для кнопки и полосы прокрутки
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            .visibility-button:hover {
                background-color: rgba(255, 152, 0, 0.1) !important;
            }
            .visibility-button:not(:hover) {
                background-color: transparent !important;
            }
            
            /* Адаптивная полоса прокрутки для таблицы ActiveProxies */
            .active-proxies-table-container::-webkit-scrollbar {
                height: 24px; /* В 3 раза толще стандартной */
            }
            
            .active-proxies-table-container::-webkit-scrollbar-track {
                background: var(--dark-gray);
                border-radius: 12px;
            }
            
            /* Светлая тема - оранжевая полоса */
            [data-theme="light"] .active-proxies-table-container::-webkit-scrollbar-thumb {
                background: var(--primary-orange);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="light"] .active-proxies-table-container::-webkit-scrollbar-thumb:hover {
                background: var(--secondary-orange);
            }
            
            /* Темная тема - светлая серая полоса */
            [data-theme="dark"] .active-proxies-table-container::-webkit-scrollbar-thumb,
            :root:not([data-theme="light"]) .active-proxies-table-container::-webkit-scrollbar-thumb {
                background: var(--light-gray);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="dark"] .active-proxies-table-container::-webkit-scrollbar-thumb:hover,
            :root:not([data-theme="light"]) .active-proxies-table-container::-webkit-scrollbar-thumb:hover {
                background: #B0B0B0;
            }
            
            /* Для Firefox - светлая тема */
            [data-theme="light"] .active-proxies-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--primary-orange) var(--dark-gray);
            }
            
            /* Для Firefox - темная тема */
            [data-theme="dark"] .active-proxies-table-container,
            :root:not([data-theme="light"]) .active-proxies-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--light-gray) var(--dark-gray);
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])

    // console.log(activeProxiesList)

    const formatTimeLeft = (date) => {
        const diffMs = date - Date.now()
        const minutes = Math.max(0, Math.floor(diffMs / (60 * 1000) % 60))
        const hours = Math.floor(diffMs / (60 * 60 * 1000))
    
        let result = minutes + "m"
        if (hours > 0) {
            result = hours + "h " + result
        }
    
        if (result === "0m") {
            result = "now"
        }
    
        return result
    }

    // Убираем автоматический выбор первой прокси
    // useEffect(()=>{
    //     if(activeProxiesList.length > 0 && selectedRow.length === 0){
    //         setSelectedRow(activeProxiesList[0])
    //     }}
    // )

    // Обработчики для пагинации
    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    // Обновляем общее количество прокси при изменении списка
    React.useEffect(() => {
        setTotalProxies(activeProxiesList.length)
    }, [activeProxiesList])

    // Обновляем данные при каждом открытии вкладки
    React.useEffect(() => {
        const refreshData = async () => {
            try {
                if (props.setReloadActive) {
                    props.setReloadActive(true)
                }
            } catch (error) {
                console.error('Error refreshing active proxies:', error)
            }
        }
        
        refreshData()
    }, []) // Пустой массив зависимостей = выполняется только при монтировании

    // Обновляем данные при фокусе на вкладке
    React.useEffect(() => {
        const handleFocus = () => {
            if (props.setReloadActive) {
                props.setReloadActive(true)
            }
        }

        // Добавляем обработчик для события focus
        window.addEventListener('focus', handleFocus)
        
        // Очищаем обработчик при размонтировании
        return () => {
            window.removeEventListener('focus', handleFocus)
        }
    }, [props.setReloadActive])

    // Получаем данные для текущей страницы с дефолтной сортировкой
    const getCurrentPageData = () => {
        // Сортируем по времени истечения (больше времени сверху)
        const sortedList = [...activeProxiesList].sort((a, b) => {
            const timeA = a.until || 0
            const timeB = b.until || 0
            return timeB - timeA // desc = больше времени сверху
        })
        
        const startIndex = page * rowsPerPage
        const endIndex = startIndex + rowsPerPage
        return sortedList.slice(startIndex, endIndex)
    }

    // Функция для вычисления возраста прокси
    const calculateProxyAge = (firstSeen) => {
        if (!firstSeen) return 'N/A'
        
        const now = moment()
        const firstSeenDate = moment(firstSeen)
        
        const years = now.diff(firstSeenDate, 'years')
        if (years >= 1) return `${years}y`
        
        const months = now.diff(firstSeenDate, 'months')
        if (months >= 1) return `${months}m`
        
        const days = now.diff(firstSeenDate, 'days')
        if (days >= 1) return `${days}d`
        
        const hours = now.diff(firstSeenDate, 'hours')
        return `${hours}h`
    }

    
    const rowClick = (row) => {
        setSelectedRow(row)
    }
    
    // const handleClick = (event, row) => {
        // console.log(row)
        // const diffMs = row?.until - Date.now()
        // const REFUND_MAX_HOURS = 3
        // const hoursUsed = 24 - Math.floor(diffMs / (60 * 60 * 1000))

        // if (hoursUsed < REFUND_MAX_HOURS) {
        //     setIsRefund(true)
        // }
        // console.log(diffMs)
        // setAnchorEl(event.currentTarget)
        // setSelectedRow(row)
    // }

    const handleClick = (row, event) => {
        setSelectedRow(row)
        setOpenDetails(true)
    }
    
    
    const handleClose = (key, row) => {
        // console.log(row)
        setSelectedRow(row)
    }

    // Обработчики для заметок
    const handleEditNote = (agentId) => {
        setEditingNoteFor(agentId)
    }

    const handleSaveNote = (agentId, note) => {
        setNotes(prev => ({ ...prev, [agentId]: note }))
        setEditingNoteFor(null)
    }

    const handleCancelNote = () => {
        setEditingNoteFor(null)
    }


    return (
        <>
        {activeProxiesList.length > 0 ? (
            <div style={{ 
                width: isMobile ? '100%' : 'calc(100% - 280px)',
                height: '100vh',
                padding: isMobile ? '0' : '8px',
                background: 'var(--black)',
                marginLeft: isMobile ? '0' : '280px',
                overflow: isMobile ? 'hidden' : 'auto'
            }}>
                {/* Мобильная навигация */}
                {isMobile && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 'var(--spacing-md)',
                        background: 'var(--dark-gray)',
                        borderBottom: '1px solid var(--light-gray)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 100
                    }}>
                        <AnimatedBurgerIcon
                            isOpen={mobileMenuOpen}
                            onClick={() => setMobileMenuOpen(true)}
                        />
                        <h1 style={{ 
                            margin: 0,
                            fontWeight: 600,
                            fontSize: 'var(--text-lg)',
                            background: 'var(--gradient-orange)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}>
                            Active Proxies
                        </h1>
                        <div style={{ width: '32px' }} /> {/* Spacer for centering */}
                    </div>
                )}

                {/* Мобильное меню */}
                {isMobile && (
                    <MobileMenu 
                        user={props.user} 
                        active={props.active} 
                        apiClient={props.apiClient}
                        isOpen={mobileMenuOpen}
                        onClose={() => setMobileMenuOpen(false)}
                    />
                )}

                {/* Контент */}
                <div style={{
                    height: isMobile ? 'calc(100vh - 60px)' : 'auto',
                    overflow: isMobile ? 'auto' : 'visible',
                    padding: isMobile ? 'var(--spacing-sm)' : '0'
                }}>
                    {!isMobile && (
                        <h1 style={{ 
                            marginTop: '8px',
                            marginBottom: '8px',
                            marginLeft: '0px',
                            fontWeight: 600,
                            fontSize: 'var(--text-2xl)',
                            background: 'var(--gradient-orange)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}>
                            Active Proxies
                        </h1>
                    )}
                
                {/* Мобильная версия таблицы */}
                {isMobile && (
                    <MobileActiveProxiesTable
                        activeProxiesList={activeProxiesList}
                        selectedRow={selectedRow}
                        onRowClick={rowClick}
                        onDetailsClick={handleClick}
                        apiClient={props.apiClient}
                    />
                )}
                
                {/* Десктопная версия таблицы */}
                {!isMobile && (
                <>
                <div className="table-container active-proxies-table-container" style={{ 
                    marginTop: '4px', 
                    marginLeft: '0px',
                    overflowX: 'auto'
                }}>
                    <table className="table" style={{ minWidth: '1000px' }}>
                        <thead>
                            <tr>
                                <th>IP</th>
                                <th>Country</th>
                                <th>State</th>
                                <th>City</th>
                                <th>Zip</th>
                                <th style={{ width: '200px' }}>ISP</th>
                                <th style={{ textAlign: 'center' }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center'
                                    }}>
                                        <span style={{ position: 'relative', display: 'inline-block' }}>
                                            <span 
                                                style={{
                                                    cursor: 'help'
                                                }}
                                                onMouseEnter={(e) => {
                                                    const tooltip = e.target.nextElementSibling;
                                                    if (tooltip) tooltip.style.display = 'block';
                                                }}
                                                onMouseLeave={(e) => {
                                                    const tooltip = e.target.nextElementSibling;
                                                    if (tooltip) tooltip.style.display = 'none';
                                                }}
                                            >
                                                Age
                                            </span>
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: 'var(--medium-gray)',
                                                color: 'var(--text-light)',
                                                padding: '8px 10px',
                                                borderRadius: 'var(--radius-sm)',
                                                fontSize: 'var(--text-xs)',
                                                whiteSpace: 'nowrap',
                                                zIndex: 10000,
                                                border: '1px solid var(--light-gray)',
                                                boxShadow: 'var(--shadow-md)',
                                                marginTop: '5px',
                                                display: 'none',
                                                maxWidth: '200px',
                                                whiteSpace: 'normal',
                                                textAlign: 'center',
                                                pointerEvents: 'none'
                                            }}>
                                                Proxy age
                                            </div>
                                        </span>
                                        /
                                        <span style={{ position: 'relative', display: 'inline-block' }}>
                                            <span 
                                                style={{
                                                    cursor: 'help'
                                                }}
                                                onMouseEnter={(e) => {
                                                    const tooltip = e.target.nextElementSibling;
                                                    if (tooltip) tooltip.style.display = 'block';
                                                }}
                                                onMouseLeave={(e) => {
                                                    const tooltip = e.target.nextElementSibling;
                                                    if (tooltip) tooltip.style.display = 'none';
                                                }}
                                            >
                                                Activity
                                            </span>
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: 'var(--medium-gray)',
                                                color: 'var(--text-light)',
                                                padding: '8px 10px',
                                                borderRadius: 'var(--radius-sm)',
                                                fontSize: 'var(--text-xs)',
                                                whiteSpace: 'nowrap',
                                                zIndex: 10000,
                                                border: '1px solid var(--light-gray)',
                                                boxShadow: 'var(--shadow-md)',
                                                marginTop: '5px',
                                                display: 'none',
                                                maxWidth: '200px',
                                                whiteSpace: 'normal',
                                                textAlign: 'center',
                                                pointerEvents: 'none'
                                            }}>
                                                Average proxy online time during the day
                                            </div>
                                        </span>
                                    </div>
                                </th>
                                <th style={{ textAlign: 'center' }}>Users</th>
                                <th>Expire at</th>
                                <th>INFO</th>
                                <th>Note</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getCurrentPageData().map((row, index) => (
                                <tr 
                                    key={row?.id || index}
                                    onClick={() => rowClick(row)}
                                    style={{
                                        backgroundColor: selectedRow?.id === row?.id ? 'var(--dark-gray)' : 'transparent',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <td>
                                        <Tooltip title={'Proxy ID: ' + row?.id} placement="top">
                                            <span>{row?.info?.ip}</span>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <ReactCountryFlag 
                                                countryCode={row?.info?.loc?.cc} 
                                                svg 
                                                style={{width: '16px', height: '12px'}}
                                            />
                                            {row?.info?.loc?.cc}
                                        </div>
                                    </td>
                                    <td>{row?.info?.loc?.reg?.replace(/(.{10})..+/, "$1…")}</td>
                                    <td>{row?.info?.loc?.city?.replace(/(.{10})..+/, "$1…")}</td>
                                    <td>{row?.info?.loc?.zip || ''}</td>
                                    <td style={{ width: '200px' }}>{row?.info?.loc?.isp}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        {row?.info?.firstSeen !== undefined ? (
                                            <span style={{ 
                                                color: 'var(--primary-orange)',
                                                fontWeight: '600'
                                            }}>
                                                {calculateProxyAge(row?.info?.firstSeen)}/{Math.round((row?.info?.probeActv || 0) * 100)}%
                                            </span>
                                        ):(
                                            <span style={{ color: 'var(--text-muted)' }}>N/A</span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>{row?.currentInfo?.leases?.count || row?.info?.leases?.count}</td>
                                    <td>{formatTimeLeft(row?.until)}</td>
                                    <td>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleClick(row, e)
                                            }}
                                            className="visibility-button"
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: 'var(--primary-orange)',
                                                padding: '4px',
                                                borderRadius: 'var(--radius-sm)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'background-color 0.2s ease'
                                            }}
                                        >
                                            <Visibility style={{ fontSize: '32px' }} />
                                        </button>
                                    </td>
                                    <td style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                                        {editingNoteFor === (row?.agentId || row?.id) ? (
                                            <NoteEditor
                                                proxy={row}
                                                apiClient={props.apiClient}
                                                onSave={(note) => handleSaveNote(row?.agentId || row?.id, note)}
                                                onCancel={handleCancelNote}
                                            />
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleEditNote(row?.agentId || row?.id)
                                                    }}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: 'var(--primary-orange)',
                                                        cursor: 'pointer',
                                                        fontSize: 'var(--text-sm)',
                                                        padding: '2px 4px',
                                                        borderRadius: 'var(--radius-sm)',
                                                        transition: 'var(--transition-normal)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.backgroundColor = 'var(--dark-gray)'
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.backgroundColor = 'transparent'
                                                    }}
                                                >
                                                    ✏️
                                                </button>
                                                <span style={{
                                                    fontSize: 'var(--text-sm)',
                                                    color: notes[row?.agentId || row?.id] || row?.note ? 'var(--text-light)' : 'var(--text-muted)',
                                                    fontStyle: notes[row?.agentId || row?.id] || row?.note ? 'normal' : 'italic'
                                                }}>
                                                    {(notes[row?.agentId || row?.id] || row?.note || 'No note').length > 10 
                                                        ? (notes[row?.agentId || row?.id] || row?.note || 'No note').substring(0, 10) + '...'
                                                        : (notes[row?.agentId || row?.id] || row?.note || 'No note')
                                                    }
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                
                {/* Панель управления пагинацией */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 'var(--spacing-md)',
                    background: 'var(--medium-gray)',
                    border: '1px solid var(--light-gray)',
                    borderRadius: 'var(--radius-lg)',
                    marginTop: 'var(--spacing-md)'
                }}>
                    {/* Строка с количеством строк на странице */}
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 'var(--spacing-sm)' 
                    }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                            Rows per page:
                        </span>
                        <select 
                            value={rowsPerPage}
                            onChange={handleChangeRowsPerPage}
                            style={{
                                background: 'var(--dark-gray)',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '6px 8px',
                                fontSize: 'var(--text-sm)',
                                minWidth: '60px'
                            }}
                        >
                            {[10, 20, 30, 40, 50].map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                    
                    {/* Навигация */}
                    <PaginationActions 
                        page={page}
                        rowsPerPage={rowsPerPage}
                        count={totalProxies}
                        onPageChange={handleChangePage}
                    />
                </div>
                </>
                )}
                
                <ProxyDetailsDialog 
                    open={openDetails} 
                    selectedProxy={selectedRow} 
                    setOpenDetails={setOpenDetails} 
                    setReloadActive={props.setReloadActive}
                    apiClient={props.apiClient}
                />
                </div>
            </div>
            ):(
                <EmptyTable name="Active Proxies" text="You don't have any active proxies."/>
            )
        }
        </>
    )
}

export default ActiveProxiesTable